import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Autocomplete,
  Popover,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ListAltIcon from '@mui/icons-material/ListAlt';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import PlanningPuzzleDialog from './PlanningPuzzleDialog';
import KlantplanningImport from './KlantplanningImport';
import { notitieInvoegen, klantPinsVoor, soortLabel } from '../../utils/klantplanning';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { formatDateISO } from '../../utils/weekUtils';
import {
  telWerkdagen, berekenPlanning, pinLimieten, aantalFeedbackRondes, vroegsteStart, formatenLijst, korteDatum,
} from '../../utils/postproductiePlanning';

const FORMAAT_OPTIES = ['16:9', '9:16', '1:1', '4:5', '4:3', '2.35:1'];

// Type uitvoerder colors (for assignee selection)
const typeColors = {
  'Editor': '#3b82f6',
  'Animator': '#ec4899',
  'Illustrator': '#f97316',
  'Projectleider': '#10b981',
  'Copywriter': '#8b5cf6',
  'Regisseur': '#7c3aed',
  'Cameraman': '#06b6d4',
  'Algemeen': '#6b7280',
  'Creative': '#a855f7',
  'Ondertitelen': '#14b8a6',
  'Productie-assistent': '#f59e0b',
  // Task type colors (for display in task list)
  'Montage': '#3b82f6',
  'Animatie': '#ec4899',
  'Illustratie': '#f97316',
  'Preproductie': '#10b981',
  'Script': '#8b5cf6',
  'Regie': '#7c3aed',
  'Draaidag': '#f59e0b',
  'Intern': '#6b7280',
  'Vrije dag': '#fbbf24',
  'Projectafronding': '#a5b4fc',
  'Marketing': '#6ee7b7',
};

// Get initials from name
// Offerteregels (Gripp) → uren per deliverable. Een offerte is per sectie
// ("Postproductie Testimonials", "Postproductie Reels") opgebouwd; de
// montage-uren staan als regel "Monteren 24 uur" en gelden voor álle films in
// die sectie. Lars: "als er 3 snippets staan en 18u, dan is het 6u per stuk."
const IS_MONTAGE = /monteren|montage|edit|kleurcorrectie|colorgrad|afmixen|geluidsnabewerking/i;
const IS_ANIMATIE = /animeren|animatie|animation|motion|illustr|storyboard/i;
const IS_AANTAL = /film|video|stuk|snippet|reel|podcast|aflevering|item|versie|variant|social/i;
function offerteGroepenNaarUren(groups) {
  return (groups || []).map((g) => {
    const lijnen = g.lines || [];
    const som = (test) => lijnen.filter((l) => test.test(`${l.description_short || ''} ${l.description || ''}`))
      .reduce((acc, l) => acc + (parseFloat(l.hours) || 0), 0);
    // Het aantal deliverables in de sectie: de eerste regel die niet in uren
    // maar in films/stuks/snippets telt. Ontbreekt die, dan 1.
    const telregel = lijnen.find((l) => (l.unit || '').toLowerCase() !== 'uur' && IS_AANTAL.test(`${l.unit || ''} ${l.description_short || ''}`) && parseFloat(l.quantity) > 0);
    return { naam: g.group || 'Overig', montage: som(IS_MONTAGE), animatie: som(IS_ANIMATIE), totaalUren: parseFloat(g.hours) || 0,
      aantal: telregel ? Math.round(parseFloat(telregel.quantity)) : 1, telEenheid: telregel ? (telregel.unit || '') : '' };
  }).filter((g) => g.montage > 0 || g.animatie > 0);
}
// Welke sectie hoort bij deze deliverable: de meeste gedeelde woorden in de naam.
function kiesOfferteGroep(groepen, deliverableNaam) {
  const woorden = new Set(String(deliverableNaam || '').toLowerCase().split(/[^a-z0-9à-ÿ]+/).filter((w) => w.length > 2));
  let beste = null; let besteScore = 0;
  groepen.forEach((g) => {
    const score = String(g.naam).toLowerCase().split(/[^a-z0-9à-ÿ]+/).filter((w) => woorden.has(w)).length;
    if (score > besteScore) { beste = g; besteScore = score; }
  });
  return beste || (groepen.length === 1 ? groepen[0] : null);
}

// Welke urenpot een rol uit de offerte gebruikt. Spiegel van
// URENPOT_PER_UITVOERDER in ai-engine/api/frontdoor/tasks.py — liep dat uiteen,
// dan toonde het voorbeeld andere uren dan de taken die uiteindelijk ontstonden.
const URENPOT_PER_TYPE = {
  Editor: 'montage', Animator: 'animatie', Illustrator: 'animatie',
  Projectleider: 'preproductie', Copywriter: 'preproductie',
  Regisseur: 'preproductie', Algemeen: 'montage', Creative: 'animatie',
  Ondertitelen: 'montage', 'Productie-assistent': 'preproductie',
};

// Verdeel één urenpot naar gewicht over taken, in halve uren, zó dat de som
// exact de pot blijft. Elke taak los naar boven afronden (het oude gedrag)
// maakte van 14 geoffreerde uren zo 16 of meer.
function verdeelUrenPot(pot, gewichten) {
  const ids = Object.keys(gewichten);
  const uit = {};
  if (!ids.length) return uit;
  const totaal = ids.reduce((som, id) => som + (gewichten[id] || 0), 0);
  const eenheden = Math.round((parseFloat(pot) || 0) * 2); // rekenen in halve uren
  if (eenheden <= 0 || totaal <= 0) {
    ids.forEach((id) => { uit[id] = 0; });
    return uit;
  }

  const ideaal = {};
  const deel = {};
  ids.forEach((id) => {
    ideaal[id] = (eenheden * (gewichten[id] || 0)) / totaal;
    deel[id] = Math.floor(ideaal[id]);
  });
  // Het restant gaat naar de grootste afgekapte fractie (largest remainder);
  // bij gelijkspel naar het zwaarste werk.
  const rest = eenheden - ids.reduce((som, id) => som + deel[id], 0);
  const volgorde = [...ids].sort((a, b) =>
    ((ideaal[b] - deel[b]) - (ideaal[a] - deel[a])) || ((gewichten[b] || 0) - (gewichten[a] || 0)));
  for (let i = 0; i < rest; i += 1) deel[volgorde[i % volgorde.length]] += 1;

  // Geen taak van nul uur zolang de pot dat toelaat: haal een half uur weg bij
  // de ruimste taak.
  if (eenheden >= ids.length) {
    ids.filter((id) => deel[id] === 0).forEach((id) => {
      const gever = ids.reduce((a, b) => (deel[b] > deel[a] ? b : a), ids[0]);
      if (deel[gever] > 1) { deel[gever] -= 1; deel[id] = 1; }
    });
  }
  ids.forEach((id) => { uit[id] = deel[id] / 2; });
  return uit;
}

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

// Capacity color helpers (matching WeekOverview)
const getCapacityColor = (percentage) => {
  if (percentage >= 100) return '#dc2626'; // red
  if (percentage >= 80) return '#ea580c';  // orange
  if (percentage >= 50) return '#ca8a04';  // yellow
  return '#16a34a'; // green
};

const getCapacityBgColor = (percentage) => {
  if (percentage >= 100) return '#fee2e2';
  if (percentage >= 80) return '#ffedd5';
  if (percentage >= 50) return '#fef9c3';
  return '#dcfce7';
};

// Template Card Component
function TemplateCard({ template, selected, onClick }) {
  const name = template.fields?.Name || 'Template';
  const taskCount = template.fields?.['Aantal taken'] || template.fields?.StandardTasks?.length || 0;
  const subtaskCount = template.fields?.TotalSubstappen || 0;
  const types = template.fields?.['Soort taken'] || [];
  const uniqueTypes = [...new Set(Array.isArray(types) ? types : [types])].filter(Boolean);

  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: selected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
        backgroundColor: selected ? '#eff6ff' : '#fff',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: selected ? '#3b82f6' : '#d1d5db',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#111827' }}>
          {name}
        </Typography>
        {selected && <CheckCircleIcon sx={{ color: '#3b82f6', fontSize: 22 }} />}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TaskAltIcon sx={{ fontSize: 16, color: '#6b7280' }} />
          <Typography variant="body2" color="text.secondary">
            <strong>{taskCount}</strong> taken
          </Typography>
        </Box>
        {subtaskCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ListAltIcon sx={{ fontSize: 16, color: '#6b7280' }} />
            <Typography variant="body2" color="text.secondary">
              <strong>{subtaskCount}</strong> subtaken
            </Typography>
          </Box>
        )}
      </Box>

      {uniqueTypes.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {uniqueTypes.slice(0, 4).map((type, idx) => (
            <Chip
              key={idx}
              label={type}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                backgroundColor: typeColors[type] || '#e5e7eb',
                color: '#fff',
                fontWeight: 500,
              }}
            />
          ))}
          {uniqueTypes.length > 4 && (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              +{uniqueTypes.length - 4}
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
}

// Main Component
function TaskTemplateGenerator({ open, onClose, deliverable, alleDeliverables = [], draaidagen = [], projectId, project, onSuccess }) {
  const [activeStep, setActiveStep] = useState(0);
  // Dezelfde planning in één keer op alle deliverables toepassen. Uit de feedback:
  // "als alle deliverables dezelfde planning moeten hanteren, wil je die eenmalig
  // genereren" — anders loop je dezelfde wizard vier keer door.
  // Welke andere deliverables dezelfde planning krijgen: { [deliverableId]: true }.
  const [kopieerNaar, setKopieerNaar] = useState({});
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetails, setTemplateDetails] = useState(null);
  const [startDate, setStartDate] = useState(formatDateISO(new Date()));
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14); // Default 2 weeks
    return d.toISOString().split('T')[0];
  });
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // States for assignees and hours
  const [personen, setPersonen] = useState([]);
  const [typeAssignees, setTypeAssignees] = useState({});

  // Hours input per type (from quote/offerte)
  const [urenMontage, setUrenMontage] = useState('');
  const [urenAnimatie, setUrenAnimatie] = useState('');
  const [urenPreproductie, setUrenPreproductie] = useState('');
  // Offerteregels uit Gripp: per sectie de montage-/animatie-uren en het aantal
  // deliverables waarover die uren gaan.
  const [offerteGroepen, setOfferteGroepen] = useState(null); // null = nog niet geladen, [] = niets bruikbaars
  const [offerteGroepNaam, setOfferteGroepNaam] = useState('');
  const [offerteVerdeelOver, setOfferteVerdeelOver] = useState(1);

  // Offerteregels info popover
  const [offerteInfoAnchor, setOfferteInfoAnchor] = useState(null);

  // Track if deadline is missing or in the past (using default)
  const [deadlineMissing, setDeadlineMissing] = useState(false);
  const [deadlineInPast, setDeadlineInPast] = useState(false);

  // Custom ZwaarteStap overrides per task (taskId -> value)
  const [customZwaarteStap, setCustomZwaarteStap] = useState({});

  // Custom task names (taskId -> name) and excluded tasks (taskId -> true)
  const [customTaskNames, setCustomTaskNames] = useState({});
  const [excludedTasks, setExcludedTasks] = useState({});

  // Extra tasks: ondertiteling, thumbnail, proxies
  const [ondertitelNodig, setOndertitelNodig] = useState(false);
  const [thumbnailNodig, setThumbnailNodig] = useState(false);
  const [proxiesNodig, setProxiesNodig] = useState(false);

  // Creative Lead Review
  const [creativeleadEnabled, setCreativeleadEnabled] = useState(false);
  const [creativeleadAssignee, setCreativeleadAssignee] = useState(null);
  const [creativeleadHours, setCreativeleadHours] = useState(1);

  // Capacity data for assignee selection
  const [capacityData, setCapacityData] = useState({});
  const [loadingCapacity, setLoadingCapacity] = useState(false);

  // Planning puzzle dialog
  const [puzzleDialogOpen, setPuzzleDialogOpen] = useState(false);

  // Feedback days between versions (configurable here, passed to puzzle)
  const [feedbackDays, setFeedbackDays] = useState(3);

  // Custom hours overrides per task (taskId -> hours)
  const [customTaskHours, setCustomTaskHours] = useState({});

  // Per-task assignee overrides (taskId -> person object)
  const [taskAssigneeOverrides, setTaskAssigneeOverrides] = useState({});

  // Puzzle adjustments (from Planning Puzzle dialog)
  const [puzzleDurationOverrides, setPuzzleDurationOverrides] = useState({}); // taskId -> days
  const [puzzleTaskOffsets, setPuzzleTaskOffsets] = useState({}); // taskId -> offset days
  const [puzzlePlanningOffset, setPuzzlePlanningOffset] = useState(0); // global start offset
  const [puzzleHoursOverrides, setPuzzleHoursOverrides] = useState({}); // taskId -> hours

  // Planning modus: 'zelf' = zelf inplannen, 'planner' = planner laat inplannen
  const [planningMode, setPlanningMode] = useState('planner');

  // Ideale rollen (suggesties van PL aan planner) - per type
  const [idealAssignees, setIdealAssignees] = useState({}); // { type: [personId, personId] }

  // Planning opmerkingen van PL aan planner
  const [planningNotes, setPlanningNotes] = useState('');

  // Vaste dagen van de klant (besluit Joris): overgenomen mijlpalen + aan welke
  // taak elke mijlpaal hangt (mijlpaalId → taakId, '' = niet vastzetten).
  const [klantMijlpalen, setKlantMijlpalen] = useState([]);
  const [pinKoppeling, setPinKoppeling] = useState({});

  // Deliverable form data (editable in step 1)
  const [deliverableForm, setDeliverableForm] = useState({});
  const [savingDeliverable, setSavingDeliverable] = useState(false);

  const steps = ['Deliverable', 'Template', 'Taken', 'Planning', 'Uren', 'Uitvoerders', 'Genereren'];

  // Get sorted tasks (all tasks from template)
  const allSortedTasks = useMemo(() => {
    if (!templateDetails?.tasks) return [];
    return [...templateDetails.tasks].sort((a, b) => {
      const sortA = a.fields?.Sort ?? 999;
      const sortB = b.fields?.Sort ?? 999;
      return sortA - sortB;
    });
  }, [templateDetails]);

  // Get active tasks (excluding removed ones)
  const sortedTasks = useMemo(() => {
    return allSortedTasks.filter(task => !excludedTasks[task.id]);
  }, [allSortedTasks, excludedTasks]);

  // Get unique 'Type Uitvoerder' from tasks (Editor, Animator, Copywriter, etc.)
  const uniqueTaskTypes = useMemo(() => {
    if (!sortedTasks.length) return [];
    const types = sortedTasks.map(t => t.fields?.['Type Uitvoerder'] || 'Algemeen');
    return [...new Set(types)].filter(t => t !== 'Algemeen' && t !== 'Intern'); // Filter out non-assignable types
  }, [sortedTasks]);

  // Calculate working days between dates (skip weekends)
  const workingDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return Math.max(1, telWerkdagen(startDate, endDate));
  }, [startDate, endDate]);

  // Default version weights: v intern=4, v1=2, v2=2, DEF=1
  // v intern gets higher weight when it's the first version (before v1)
  const VERSION_WEIGHTS = [
    { pattern: /v\s*intern|intern/i, weight: 4 },
    { pattern: /v\s*1\b|v1\b/i, weight: 2 },
    { pattern: /v\s*2\b|v2\b/i, weight: 2 },
    { pattern: /\bdef\b|definitief|final/i, weight: 1 },
  ];

  // Get ZwaarteStap for a task (custom override > vaste stap > version-based default > Airtable value > 1)
  const getZwaarteStap = (task) => {
    // Custom override takes priority
    if (customZwaarteStap[task.id] !== undefined && customZwaarteStap[task.id] !== '') {
      return customZwaarteStap[task.id];
    }
    // Een expliciete 0 in het sjabloon betekent "vaste stap": deze taak deelt
    // niet mee in de geoffreerde uren maar houdt zijn eigen AantalUren
    // (bv. "Interne check PL" = altijd 1 uur).
    if (task.fields?.ZwaarteStap === 0) return 0;
    // Een bewust gezette ZwaarteStap wint van het versiepatroon. Andersom
    // (patroon eerst) kreeg "Interne check STAGIAIRE 2" gewicht 4 omdat er
    // "intern" in staat, en negeerde het voorbeeld de waarde uit het sjabloon —
    // terwijl de engine die juist wél gebruikt.
    if (task.fields?.ZwaarteStap && task.fields.ZwaarteStap > 1) {
      return task.fields.ZwaarteStap;
    }
    // Anders (waarde 1 = de Airtable-standaard, dus niet te onderscheiden van
    // "niet ingevuld") het versiepatroon: v intern 4, v1 2, v2 2, DEF 1.
    const name = task.fields?.Name || '';
    for (const { pattern, weight } of VERSION_WEIGHTS) {
      if (pattern.test(name)) return weight;
    }
    return 1;
  };

  // Uren per pot uit de offerte.
  const potUren = {
    montage: parseFloat(urenMontage) || 0,
    animatie: parseFloat(urenAnimatie) || 0,
    preproductie: parseFloat(urenPreproductie) || 0,
  };

  // De zwaarte die we daadwerkelijk gebruiken (override > versiepatroon >
  // Airtable). Gaat ook mee in de payload, zodat de engine exact zo verdeelt
  // als het voorbeeld hier laat zien.
  const effectieveZwaarte = useMemo(() => {
    const result = {};
    sortedTasks.forEach(task => { result[task.id] = getZwaarteStap(task); });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedTasks, customZwaarteStap]);

  // Uren verdelen per URENPOT, niet per rol: Editor en Algemeen putten allebei
  // uit de montage-uren. Per rol verdelen gaf elke rol de vólle pot — twee
  // rollen op dezelfde pot betekende dus dubbele uren.
  const autoUrenPerTaak = useMemo(() => {
    const perPot = {};
    const vast = {};
    sortedTasks.forEach(task => {
      const zwaarte = effectieveZwaarte[task.id] ?? 1;
      if (zwaarte <= 0) {
        // Vaste stap: uren uit het sjabloon, buiten de verdeling om.
        vast[task.id] = parseFloat(task.fields?.AantalUren) || 0;
        return;
      }
      const type = task.fields?.['Type Uitvoerder'] || 'Algemeen';
      const pot = URENPOT_PER_TYPE[type];
      if (!pot) return;
      (perPot[pot] = perPot[pot] || {})[task.id] = zwaarte;
    });
    let uit = { ...vast };
    Object.entries(perPot).forEach(([pot, gewichten]) => {
      uit = { ...uit, ...verdeelUrenPot(potUren[pot], gewichten) };
    });
    return uit;
  // potUren is elke render een nieuw object; de drie urenvelden zijn de echte afhankelijkheid.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedTasks, effectieveZwaarte, urenMontage, urenAnimatie, urenPreproductie]);

  // Feedbackrondes tussen versies (v intern → v1 → v2 → DEF): dezelfde regel als de engine.
  const takenNamen = useMemo(() => sortedTasks.map((t) => (
    customTaskNames[t.id] !== undefined ? customTaskNames[t.id] : (t.fields?.Name || '')
  )), [sortedTasks, customTaskNames]);
  const feedbackTransitions = useMemo(() => aantalFeedbackRondes(takenNamen), [takenNamen]);

  // Wachtdagen gaan eerst van de werkdagen af (feedback, puzzel-verschuivingen,
  // de review-dag van de Creative Lead); alleen de rest wordt over de taken
  // verdeeld. Zelfde rekensom als genereer_taken in de engine.
  const totalFeedbackDays = feedbackTransitions * (parseInt(feedbackDays) || 0);
  // Alleen verschuivingen van taken die nog meedoen: een uitgesloten taak kost
  // in de engine ook geen dagen.
  const puzzelWachtdagen = Math.max(0, puzzlePlanningOffset || 0)
    + sortedTasks.reduce((som, t) => som + Math.max(0, puzzleTaskOffsets[t.id] || 0), 0)
    + (creativeleadEnabled ? 1 : 0);
  const effectiveWorkingDays = Math.max(1, workingDays - totalFeedbackDays - puzzelWachtdagen);

  // Wat de puzzel nodig heeft om zelf live mee te rekenen (zelfde functie als hier).
  const planTaken = useMemo(() => sortedTasks.map((t, i) => ({
    id: t.id, name: takenNamen[i], zwaarte: getZwaarteStap(t),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [sortedTasks, takenNamen, customZwaarteStap]);

  // Dagen per taak precies zoals de engine ze opslaat: wachtdagen eerst,
  // verdeling naar zwaarte, puzzel-duren erover, dan inkorten tot de deadline.
  const klantPins = useMemo(() => klantPinsVoor(klantMijlpalen, planTaken, pinKoppeling),
    [klantMijlpalen, planTaken, pinKoppeling]);
  const planUitkomst = useMemo(() => berekenPlanning(planTaken, {
    werkdagen: telWerkdagen(startDate, endDate),
    feedbackDagen: feedbackDays,
    planningOffset: puzzlePlanningOffset,
    taakOffsets: puzzleTaskOffsets,
    duurOverrides: puzzleDurationOverrides,
    reviewDag: creativeleadEnabled,
    pinLimieten: pinLimieten(klantPins, { startIso: startDate, feedbackDagen: feedbackDays, taakIds: planTaken.map((t) => t.id) }),
  }), [planTaken, startDate, endDate, feedbackDays, puzzlePlanningOffset, puzzleTaskOffsets,
    puzzleDurationOverrides, creativeleadEnabled, klantPins]);
  const dagenPerTaak = planUitkomst.duren;

  // Calculate planned tasks with days and hours for Planning Puzzle dialog
  const plannedTasks = useMemo(() => {
    if (!sortedTasks.length) return [];

    return sortedTasks.map(task => {
      const typeUitvoerder = task.fields?.['Type Uitvoerder'] || 'Algemeen';
      const taskName = customTaskNames[task.id] !== undefined
        ? customTaskNames[task.id]
        : (task.fields?.Name || 'Taak');

      const days = dagenPerTaak[task.id] || 1;

      // Uren komen uit de potverdeling: die telt exact op tot de geoffreerde uren.
      const hours = customTaskHours[task.id] !== undefined
        ? customTaskHours[task.id]
        : (autoUrenPerTaak[task.id] || 0);

      return {
        id: task.id,
        name: taskName,
        type: typeUitvoerder,
        days,
        hours,
        // De puzzel rekent de duren zelf live na (berekenPlanning) op basis van de zwaarte.
        zwaarte: getZwaarteStap(task),
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedTasks, dagenPerTaak, customTaskNames, autoUrenPerTaak, customTaskHours, customZwaarteStap]);

  // Filter templates based on search query
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(t => {
      const name = (t.fields?.Name || '').toLowerCase();
      const types = (t.fields?.['Soort taken'] || []).join(' ').toLowerCase();
      return name.includes(query) || types.includes(query);
    });
  }, [templates, searchQuery]);

  // Voor welke deliverable de wizard nu open staat. Na het genereren roept de
  // ouder loadData() aan: project en deliverable komen dan als NIEUWE objecten
  // binnen, en omdat dit effect daarop reageerde, sprong de wizard terug naar
  // stap 1 ("ik ben door de wizard gelopen en krijg nu dit scherm weer").
  // Resetten doen we dus alleen bij openen of bij een andere deliverable.
  const geopendVoor = useRef(null);
  const [laatsteDraaidag, setLaatsteDraaidag] = useState(null);
  const [vroegsteStartDatum, setVroegsteStartDatum] = useState(null);

  // Fetch templates and personen on open
  useEffect(() => {
    if (!open) {
      geopendVoor.current = null;
      return;
    }
    const sleutel = deliverable?.id || '__zonder__';
    if (geopendVoor.current === sleutel) return;
    geopendVoor.current = sleutel;
    {
      fetchTemplates();
      fetchPersonen();
      // Reset state
      setActiveStep(0);
      setSelectedTemplate(null);
      setTemplateDetails(null);
      setResult(null);
      setError(null);
      setTypeAssignees({});
      setCustomZwaarteStap({});
      setCustomTaskNames({});
      setExcludedTasks({});
      setCustomTaskHours({});
      setFeedbackDays(3);
      // Selectie niet laten staan van een vorige sessie: onbedoeld nogmaals
      // taken genereren voor andere deliverables is precies wat je niet wilt.
      setKopieerNaar({});
      setKlantMijlpalen([]);
      setPinKoppeling({});

      // Pre-fill deliverable form
      setDeliverableForm({
        'Deadline': deliverable?.fields?.Deadline || '',
        'LengteVideo': deliverable?.fields?.LengteVideo || '',
        'Ondertitel nodig': deliverable?.fields?.['Ondertitel nodig'] || false,
        'Thumbnail Nodig?': deliverable?.fields?.['Thumbnail Nodig?'] || false,
        // Meerdere formaten (16:9 én 4:5): altijd als lijst in het formulier.
        'Formaat': formatenLijst(deliverable?.fields?.Formaat),
        'Briefing': deliverable?.fields?.Briefing || '',
      });

      // Pre-fill end date from deliverable's Deadline
      const deliverableDeadline = deliverable?.fields?.Deadline;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const twoWeeksFromNow = new Date();
      twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);

      if (deliverableDeadline) {
        const deadlineDate = new Date(deliverableDeadline);
        deadlineDate.setHours(0, 0, 0, 0);

        if (deadlineDate < today) {
          // Deadline is in the past - use 2 weeks from now and show warning
          setEndDate(twoWeeksFromNow.toISOString().split('T')[0]);
          setDeadlineInPast(true);
          setDeadlineMissing(false);
        } else {
          // Deadline exists and is in the future - use it
          setEndDate(deliverableDeadline);
          setDeadlineMissing(false);
          setDeadlineInPast(false);
        }
      } else {
        // No deadline - default to 2 weeks and show warning
        setEndDate(twoWeeksFromNow.toISOString().split('T')[0]);
        setDeadlineMissing(true);
        setDeadlineInPast(false);
      }

      // Start: vandaag, maar nooit vóór de dag na de laatste draaidag —
      // postproductie kan pas als er gedraaid is.
      const vroegste = vroegsteStart(draaidagen);
      setStartDate(vroegste.datum);
      setVroegsteStartDatum(vroegste.datum);
      setLaatsteDraaidag(vroegste.laatsteDraaidag);

      // Pre-fill hours from project's offerteregels
      const offerteMontage = project?.fields?.OfferteMontageUren;
      const offerteAnimatie = project?.fields?.OfferteAnimatieUren;
      // Set hours if available (can be number or array with numbers)
      const getMontageUren = Array.isArray(offerteMontage) ? offerteMontage[0] : offerteMontage;
      const getAnimatieUren = Array.isArray(offerteAnimatie) ? offerteAnimatie[0] : offerteAnimatie;
      setUrenMontage(getMontageUren ? String(getMontageUren) : '');
      setUrenAnimatie(getAnimatieUren ? String(getAnimatieUren) : '');
      setUrenPreproductie('');

      // Offerteregels van dit project ophalen en de best passende sectie kiezen.
      setOfferteGroepen(null); setOfferteGroepNaam(''); setOfferteVerdeelOver(1);
      const nr = project?.fields?.Projectnummer || project?.projectNumber;
      if (nr) {
        authFetch(`${AI_ENGINE_URL}/api/v1/quote-lines/project/${encodeURIComponent(nr)}`)
          .then((r) => (r.ok ? r.json() : { groups: [] }))
          .then((d) => {
            const groepen = offerteGroepenNaarUren(d.groups);
            setOfferteGroepen(groepen);
            const keuze = kiesOfferteGroep(groepen, deliverable?.fields?.['Naam van Deliverable']);
            if (keuze) { setOfferteGroepNaam(keuze.naam); setOfferteVerdeelOver(keuze.aantal || 1); }
          })
          .catch(() => setOfferteGroepen([]));
      } else {
        setOfferteGroepen([]);
      }

      // Pre-fill extra task options from deliverable
      setOndertitelNodig(deliverable?.fields?.['Ondertitel nodig'] || false);
      setThumbnailNodig(deliverable?.fields?.['Thumbnail Nodig?'] || false);
      setProxiesNodig(deliverable?.fields?.['ProxiesNodig'] || false);

      // Pre-fill Creative Lead options
      setCreativeleadEnabled(deliverable?.fields?.['Meekijken door creative lead?'] || false);
      setCreativeleadHours(deliverable?.fields?.['Uren voor creative lead?'] || 1);
      // Creative Lead assignee ID: first try deliverable, then project (will be resolved once personen loads)
      const clAssigneeId = deliverable?.fields?.['AssigneeCreativeLead']?.[0] ||
                           project?.fields?.['Creative (from Project)']?.[0] || null;
      // Store the ID temporarily - will be resolved in the personen useEffect below
      setCreativeleadAssignee(clAssigneeId ? { id: clAssigneeId, _pendingResolve: true } : null);
    }
  // draaidagen/project worden bij het openen gelezen; latere nieuwe objecten
  // (loadData na genereren) mogen de wizard juist níet resetten.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project, deliverable]);

  // Resolve creativeleadAssignee when personen are loaded
  useEffect(() => {
    if (personen.length > 0 && creativeleadAssignee?._pendingResolve) {
      const found = personen.find(p => p.id === creativeleadAssignee.id);
      if (found) {
        setCreativeleadAssignee(found);
      } else {
        setCreativeleadAssignee(null);
      }
    }
  }, [personen, creativeleadAssignee]);

  // Fetch template details when selected
  useEffect(() => {
    if (selectedTemplate) {
      fetchTemplateDetails(selectedTemplate.id);
    }
  }, [selectedTemplate]);


  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/standard-task-labels`);
      if (!response.ok) throw new Error('Kon templates niet laden');
      const data = await response.json();
      setTemplates(data.labels || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonen = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/personen`);
      if (response.ok) {
        const data = await response.json();
        setPersonen(data.records || []);
      }
    } catch (err) {
      console.error('Error fetching personen:', err);
    }
  };

  // Fetch capacity data when dates or roles change (for Uitvoerders step)
  const fetchCapacity = async () => {
    if (!startDate || !endDate || uniqueTaskTypes.length === 0) return;

    setLoadingCapacity(true);
    try {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      uniqueTaskTypes.forEach(role => params.append('roles[]', role));

      const response = await authFetch(`${BACKEND_URL}/Api/capacity-by-role?${params}`);
      if (response.ok) {
        const data = await response.json();
        setCapacityData(data);
      }
    } catch (err) {
      console.error('Error fetching capacity:', err);
    } finally {
      setLoadingCapacity(false);
    }
  };

  // Sync deliverableForm changes to wizard state
  useEffect(() => {
    if (deliverableForm['Ondertitel nodig'] !== undefined) {
      setOndertitelNodig(deliverableForm['Ondertitel nodig']);
    }
    if (deliverableForm['Thumbnail Nodig?'] !== undefined) {
      setThumbnailNodig(deliverableForm['Thumbnail Nodig?']);
    }
  }, [deliverableForm]);

  // Save deliverable changes to Airtable
  const handleSaveDeliverable = async () => {
    if (!deliverable?.id) return;
    setSavingDeliverable(true);
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/deliverables/${deliverable.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deliverableForm),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Kon deliverable niet opslaan');
      }
    } catch (err) {
      console.error('Error saving deliverable:', err);
      setError(`Deliverable opslaan mislukt: ${err.message}`);
    } finally {
      setSavingDeliverable(false);
    }
  };

  // Trigger capacity fetch when moving to or past the Planning step
  useEffect(() => {
    if (open && startDate && endDate && uniqueTaskTypes.length > 0 && activeStep >= 3) {
      fetchCapacity();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startDate, endDate, uniqueTaskTypes, activeStep]);

  const fetchTemplateDetails = async (labelId) => {
    setTemplateDetails(null);
    try {
      console.log('[TaskTemplateGenerator] Fetching template details for:', labelId);
      const response = await authFetch(`${BACKEND_URL}/Api/standard-tasks/${labelId}`);
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Kon template details niet laden');
      }
      const data = await response.json();
      console.log('[TaskTemplateGenerator] Got template details:', data);

      if (!data.tasks || data.tasks.length === 0) {
        setError('Deze template heeft geen taken');
      }
      setTemplateDetails(data);
    } catch (err) {
      console.error('Error fetching template details:', err);
      setError(err.message);
    }
  };

  const handleGenerate = async () => {
    if (!selectedTemplate || !deliverable?.id) return;

    setGenerating(true);
    setError(null);

    try {
      // Build assignees map: { taskType: personId }
      const assignees = {};
      Object.entries(typeAssignees).forEach(([type, person]) => {
        if (person?.id) {
          assignees[type] = person.id;
        }
      });

      // Als losse waarde, zodat dezelfde instellingen hergebruikt kunnen worden voor
      // de andere deliverables (alleen deliverableId verschilt dan).
      const payload = {
          labelId: selectedTemplate.id,
          deliverableId: deliverable.id,
          projectId: projectId,
          startDate: startDate,
          endDate: endDate,
          planningMode: planningMode, // 'zelf' or 'planner'
          assignees: planningMode === 'zelf' ? assignees : {}, // Only use assignees in 'zelf' mode
          // Hours from quote (offerte)
          urenMontage: urenMontage ? parseFloat(urenMontage) : null,
          urenAnimatie: urenAnimatie ? parseFloat(urenAnimatie) : null,
          urenPreproductie: urenPreproductie ? parseFloat(urenPreproductie) : null,
          // De zwaarte zoals hier getoond (incl. versiepatroon-defaults), zodat de
          // engine dezelfde verdeling maakt als het voorbeeld.
          customZwaarteStap: { ...effectieveZwaarte, ...customZwaarteStap },
          // Custom task names and excluded tasks
          customTaskNames: customTaskNames,
          excludedTasks: Object.keys(excludedTasks),
          // Extra tasks
          ondertitelNodig: ondertitelNodig,
          thumbnailNodig: thumbnailNodig,
          proxiesNodig: proxiesNodig,
          // Video length for ondertiteling hours calculation
          // Uit het formulier van stap 1: daar kun je de lengte nog aanpassen.
          lengteVideo: parseFloat(deliverableForm['LengteVideo']) || deliverable?.fields?.LengteVideo || 0,
          // Creative Lead Review
          creativeleadEnabled: creativeleadEnabled,
          creativeleadAssigneeId: creativeleadAssignee?.id || null,
          creativeleadHours: creativeleadHours,
          // Per-task assignee overrides
          taskAssigneeOverrides: Object.fromEntries(
            Object.entries(taskAssigneeOverrides).map(([taskId, person]) => [taskId, person?.id])
          ),
          // Custom task hours
          customTaskHours: customTaskHours,
          // Puzzle adjustments (from Planning Puzzle)
          puzzleDurationOverrides: puzzleDurationOverrides,
          puzzleTaskOffsets: puzzleTaskOffsets,
          puzzlePlanningOffset: puzzlePlanningOffset,
          puzzleHoursOverrides: puzzleHoursOverrides,
          feedbackDays: feedbackDays,
          // Vaste dagen van de klant: deze taken moeten op/vóór die datum klaar zijn.
          klantPins: klantPins.filter((p) => p.taakId).map(({ taakId, datum, soort }) => ({ taakId, datum, soort })),
          // Ideal assignees and planning notes
          idealAssignees: idealAssignees,
          planningNotes: planningNotes,
      };

      const response = await authFetch(`${BACKEND_URL}/Api/generate-tasks-from-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Kon taken niet genereren');
      }

      const data = await response.json();

      // Dezelfde planning op de overige deliverables. Sequentieel: Airtable knijpt
      // parallelle schrijfacties af, en bij een fout wil je weten bij wélke.
      const extra = [];
      for (const d of gekozenDeliverables) {
        const naam = deliverableNaam(d);
        const deadline = deadlineVoor(d);
        // Codex 2 sep: de uren kwamen uit de offertesectie van de EERSTE deliverable.
        // Elke deliverable krijgt nu zijn eigen best passende sectie; geen match → zelfde uren.
        const eigenGroep = offerteGroepen?.length ? kiesOfferteGroep(offerteGroepen, naam) : null;
        const nEigen = eigenGroep ? Math.max(1, eigenGroep.aantal || 1) : 1;
        const eigenUren = eigenGroep ? {
          urenMontage: eigenGroep.montage ? Math.round((eigenGroep.montage / nEigen) * 2) / 2 : payload.urenMontage,
          urenAnimatie: eigenGroep.animatie ? Math.round((eigenGroep.animatie / nEigen) * 2) / 2 : payload.urenAnimatie,
        } : {};
        try {
          const r2 = await authFetch(`${BACKEND_URL}/Api/generate-tasks-from-template`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // deliverableId, deadline én LengteVideo zijn per deliverable — de
            // ondertiteluren komen uit de videolengte, en de planning moet tot de
            // eigen deadline lopen en niet tot die van deze deliverable.
            body: JSON.stringify({
              ...payload,
              ...eigenUren,
              customTaskHours: eigenGroep ? {} : payload.customTaskHours,
              // De urenpuzzel hoort bij de eerste deliverable; met een eigen
              // offertesectie zou hij de nieuwe uren server-side overschrijven.
              puzzleHoursOverrides: eigenGroep ? {} : payload.puzzleHoursOverrides,
              // De klantdatums horen bij déze deliverable; de andere plannen tot hun eigen deadline.
              klantPins: [],
              deliverableId: d.id,
              endDate: deadline.datum,
              lengteVideo: d.fields?.LengteVideo || 0,
            }),
          });
          const d2 = await r2.json();
          if (!r2.ok) throw new Error(d2.error || `HTTP ${r2.status}`);
          extra.push({ naam, created: d2.created || 0, overschrijding: d2.deadlineOverschrijding || 0,
                       deadline: deadline.datum, eigenDeadline: deadline.eigen, verlopen: deadline.verlopen,
                       sectie: eigenGroep?.naam || null, uren: eigenUren.urenMontage ?? payload.urenMontage });
        } catch (e) {
          extra.push({ naam, fout: e.message });
        }
      }
      data.extraDeliverables = extra;
      setResult(data);
      setActiveStep(6); // Move to result step

      if (onSuccess) {
        onSuccess(data);
      }

      // Auto-close after 2 seconds — maar niet als er iets te lezen valt
      // (deadline-overschrijding of de uitkomst per deliverable).
      const ietsTeMelden = (data.deadlineOverschrijding || 0) > 0 || extra.length > 0 || (data.klantpinsTeLaat || []).length > 0;
      if (!ietsTeMelden) {
        setTimeout(() => { onClose(); }, 2000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  // De overige deliverables van dit project — kandidaten voor dezelfde planning.
  const andereDeliverables = (alleDeliverables || []).filter((d) => d?.id && d.id !== deliverable?.id);

  const deliverableNaam = (d) =>
    d?.fields?.['Naam van Deliverable'] || d?.fields?.Deliverable || 'Deliverable';

  // Elke deliverable plant tot zijn EIGEN deadline. Hiervoor erfde elke kopie de
  // einddatum van deze deliverable, waardoor een deliverable met een vroegere
  // deadline er standaard overheen liep.
  const deadlineVoor = (d) => {
    const eigen = d?.fields?.Deadline ? String(d.fields.Deadline).slice(0, 10) : '';
    if (!eigen) return { datum: endDate, eigen: false, verlopen: false };
    const vandaag = formatDateISO(new Date());
    if (eigen < vandaag) return { datum: endDate, eigen: false, verlopen: true };
    return { datum: eigen, eigen: true, verlopen: false };
  };

  const gekozenDeliverables = andereDeliverables.filter((d) => kopieerNaar[d.id]);

  // Voorstel uit de planning van de klant (mail/screenshot/document). Pas na
  // "Voorstel overnemen" en met ongedaan maken: nooit stil overschrijven.
  const pasKlantplanningToe = (voorstel) => {
    const vorige = { startDate, endDate, feedbackDays, planningNotes, deadlineMissing, deadlineInPast, klantMijlpalen, pinKoppeling };
    if (voorstel.startDate) setStartDate(voorstel.startDate);
    if (voorstel.endDate) {
      setEndDate(voorstel.endDate);
      setDeadlineMissing(false);
      setDeadlineInPast(false);
    }
    if (voorstel.feedbackDays) setFeedbackDays(voorstel.feedbackDays);
    if (voorstel.notitie) setPlanningNotes((n) => notitieInvoegen(n, voorstel.notitie));
    setKlantMijlpalen(voorstel.mijlpalen || []);
    setPinKoppeling({});
    biedOngedaanAan('Planning van de klant overgenomen', async () => {
      setStartDate(vorige.startDate);
      setEndDate(vorige.endDate);
      setFeedbackDays(vorige.feedbackDays);
      setPlanningNotes(vorige.planningNotes);
      setDeadlineMissing(vorige.deadlineMissing);
      setDeadlineInPast(vorige.deadlineInPast);
      setKlantMijlpalen(vorige.klantMijlpalen);
      setPinKoppeling(vorige.pinKoppeling);
    });
  };

  const deliverableName = deliverable?.fields?.['Naam van Deliverable'] ||
                          deliverable?.fields?.Deliverable ||
                          'Deliverable';

  const canProceed = () => {
    switch (activeStep) {
      case 0: return true; // Deliverable (always can proceed)
      case 1: return !!selectedTemplate; // Template
      case 2: return !!templateDetails && sortedTasks.length > 0; // Taken (at least 1 active task)
      case 3: return !!startDate && !!endDate; // Planning
      case 4: return true; // Uren (optional)
      case 5: return true; // Uitvoerders (optional)
      default: return false;
    }
  };

  const handleNext = () => {
    if (activeStep === 5) {
      // Generate tasks after Uitvoerders step
      handleGenerate();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, minHeight: 500 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AutoAwesomeIcon sx={{ color: '#3b82f6' }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Postproductie Planning Genereren
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {deliverableName}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e5e7eb' }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label} completed={activeStep > index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 0: Deliverable Details */}
        {activeStep === 0 && (
          <Box>
            {/* Header Card */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid #e5e7eb',
                mb: 3,
              }}
            >
              {/* Colored Header */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  px: 3,
                  py: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
                    {deliverable?.fields?.Name || 'Deliverable'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.25 }}>
                    Controleer de gegevens voordat je verder gaat
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveDeliverable}
                  disabled={savingDeliverable}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                  }}
                >
                  {savingDeliverable ? 'Opslaan...' : '💾 Opslaan'}
                </Button>
              </Box>

              {/* Content */}
              <Box sx={{ p: 3 }}>
                {/* Form Fields Row */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                  {/* Deadline */}
                  <Box sx={{ flex: '1 1 180px', minWidth: 150 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                      📅 Deadline
                    </Typography>
                    <TextField
                      type="date"
                      value={deliverableForm['Deadline'] || ''}
                      onChange={(e) => {
                        setDeliverableForm(prev => ({ ...prev, 'Deadline': e.target.value }));
                        if (e.target.value) setEndDate(e.target.value);
                      }}
                      size="small"
                      fullWidth
                      InputProps={{
                        sx: { borderRadius: 2, backgroundColor: '#f9fafb' }
                      }}
                    />
                  </Box>

                  {/* Lengte Video */}
                  <Box sx={{ flex: '1 1 140px', minWidth: 120 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                      ⏱️ Lengte (sec)
                    </Typography>
                    <TextField
                      type="number"
                      value={deliverableForm['LengteVideo'] || ''}
                      onChange={(e) => setDeliverableForm(prev => ({ ...prev, 'LengteVideo': e.target.value }))}
                      size="small"
                      fullWidth
                      inputProps={{ min: 0 }}
                      InputProps={{
                        sx: { borderRadius: 2, backgroundColor: '#f9fafb' }
                      }}
                    />
                  </Box>

                  {/* Formaat */}
                  <Box sx={{ flex: '1 1 140px', minWidth: 120 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                      📐 Formaten
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <Select
                        multiple
                        value={formatenLijst(deliverableForm['Formaat'])}
                        onChange={(e) => {
                          const v = e.target.value;
                          const gekozen = typeof v === 'string' ? v.split(',') : v;
                          // In de volgorde van de keuzelijst, niet van aanklikken.
                          setDeliverableForm(prev => ({ ...prev, 'Formaat': [
                            ...FORMAAT_OPTIES.filter((o) => gekozen.includes(o)),
                            ...gekozen.filter((o) => !FORMAAT_OPTIES.includes(o)),
                          ] }));
                        }}
                        displayEmpty
                        renderValue={(gekozen) => (gekozen.length ? gekozen.join(', ') : <Box component="span" sx={{ color: 'text.disabled' }}>Kies formaten</Box>)}
                        inputProps={{ 'aria-label': 'Formaten' }}
                        sx={{ borderRadius: 2, backgroundColor: '#f9fafb' }}
                      >
                        {[...FORMAAT_OPTIES, ...formatenLijst(deliverableForm['Formaat']).filter((o) => !FORMAAT_OPTIES.includes(o))].map(opt => (
                          <MenuItem key={opt} value={opt} dense>
                            <Checkbox size="small" checked={formatenLijst(deliverableForm['Formaat']).includes(opt)} sx={{ p: 0.5, mr: 1 }} />
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* Toggle Chips */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 500 }}>
                    Extra opties
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label="Ondertiteling"
                      icon={<Box component="span" sx={{ fontSize: '14px', ml: 0.5 }}>💬</Box>}
                      onClick={() => setDeliverableForm(prev => ({ ...prev, 'Ondertitel nodig': !prev['Ondertitel nodig'] }))}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        border: deliverableForm['Ondertitel nodig'] ? '2px solid #6366f1' : '1px solid #e5e7eb',
                        backgroundColor: deliverableForm['Ondertitel nodig'] ? '#ede9fe' : '#fff',
                        color: deliverableForm['Ondertitel nodig'] ? '#6366f1' : '#6b7280',
                        '&:hover': { backgroundColor: deliverableForm['Ondertitel nodig'] ? '#ddd6fe' : '#f3f4f6' },
                        transition: 'all 0.15s ease',
                      }}
                    />
                    <Chip
                      label="Thumbnail"
                      icon={<Box component="span" sx={{ fontSize: '14px', ml: 0.5 }}>🖼️</Box>}
                      onClick={() => setDeliverableForm(prev => ({ ...prev, 'Thumbnail Nodig?': !prev['Thumbnail Nodig?'] }))}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        border: deliverableForm['Thumbnail Nodig?'] ? '2px solid #6366f1' : '1px solid #e5e7eb',
                        backgroundColor: deliverableForm['Thumbnail Nodig?'] ? '#ede9fe' : '#fff',
                        color: deliverableForm['Thumbnail Nodig?'] ? '#6366f1' : '#6b7280',
                        '&:hover': { backgroundColor: deliverableForm['Thumbnail Nodig?'] ? '#ddd6fe' : '#f3f4f6' },
                        transition: 'all 0.15s ease',
                      }}
                    />
                    <Chip
                      label="Proxies maken"
                      icon={<Box component="span" sx={{ fontSize: '14px', ml: 0.5 }}>📦</Box>}
                      onClick={() => setProxiesNodig(!proxiesNodig)}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        border: proxiesNodig ? '2px solid #6366f1' : '1px solid #e5e7eb',
                        backgroundColor: proxiesNodig ? '#ede9fe' : '#fff',
                        color: proxiesNodig ? '#6366f1' : '#6b7280',
                        '&:hover': { backgroundColor: proxiesNodig ? '#ddd6fe' : '#f3f4f6' },
                        transition: 'all 0.15s ease',
                      }}
                    />
                  </Box>
                </Box>

                {/* Briefing */}
                {deliverableForm['Briefing'] && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                      📝 Briefing
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: '#f9fafb',
                        borderRadius: 2,
                        border: '1px solid #e5e7eb',
                        maxHeight: 120,
                        overflow: 'auto',
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', color: '#374151', lineHeight: 1.6 }}>
                        {deliverableForm['Briefing']}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        )}

        {/* Step 1: Select Template */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Kies een template voor de postproductie planning:
            </Typography>

            {/* Search bar */}
            <TextField
              fullWidth
              size="small"
              placeholder="Zoek template..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
            />

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                {filteredTemplates.map(template => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    selected={selectedTemplate?.id === template.id}
                    onClick={() => setSelectedTemplate(template)}
                  />
                ))}
              </Box>
            )}

            {filteredTemplates.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  {searchQuery ? 'Geen templates gevonden voor deze zoekopdracht' : 'Geen templates gevonden'}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Step 2: Edit Tasks */}
        {activeStep === 2 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Pas taaknamen aan of verwijder taken die je niet nodig hebt:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {Object.keys(excludedTasks).length > 0 && (
                  <Chip
                    label={`${Object.keys(excludedTasks).length} verwijderd`}
                    size="small"
                    sx={{ backgroundColor: '#ef4444', color: '#fff' }}
                  />
                )}
                <Chip
                  label={`${sortedTasks.length} taken`}
                  size="small"
                  sx={{ backgroundColor: '#3b82f6', color: '#fff' }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 350, overflow: 'auto' }}>
              {allSortedTasks.map(task => {
                const isExcluded = excludedTasks[task.id];
                const taskName = customTaskNames[task.id] !== undefined
                  ? customTaskNames[task.id]
                  : (task.fields?.Name || 'Taak');
                const typeUitvoerder = task.fields?.['Type Uitvoerder'] || 'Algemeen';

                return (
                  <Paper
                    key={task.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: isExcluded ? '#fecaca' : '#e5e7eb',
                      backgroundColor: isExcluded ? '#fef2f2' : '#fff',
                      opacity: isExcluded ? 0.6 : 1,
                      transition: 'all 0.2s',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {/* Delete/Restore button */}
                      <Tooltip title={isExcluded ? 'Taak herstellen' : 'Taak verwijderen'}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (isExcluded) {
                              setExcludedTasks(prev => {
                                const next = { ...prev };
                                delete next[task.id];
                                return next;
                              });
                            } else {
                              setExcludedTasks(prev => ({ ...prev, [task.id]: true }));
                              // Puzzel-aanpassingen van deze taak vervallen mee.
                              const zonder = (prev) => {
                                if (!(task.id in prev)) return prev;
                                const next = { ...prev };
                                delete next[task.id];
                                return next;
                              };
                              setPuzzleTaskOffsets(zonder);
                              setPuzzleDurationOverrides(zonder);
                              setPuzzleHoursOverrides(zonder);
                            }
                          }}
                          sx={{
                            color: isExcluded ? '#10b981' : '#9ca3af',
                            '&:hover': {
                              color: isExcluded ? '#059669' : '#ef4444',
                              backgroundColor: isExcluded ? '#d1fae5' : '#fee2e2',
                            },
                          }}
                        >
                          {isExcluded ? <RestoreIcon fontSize="small" /> : <DeleteOutlineIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>

                      {/* Editable task name */}
                      <TextField
                        value={taskName}
                        onChange={(e) => setCustomTaskNames(prev => ({ ...prev, [task.id]: e.target.value }))}
                        disabled={isExcluded}
                        size="small"
                        variant="standard"
                        fullWidth
                        InputProps={{
                          disableUnderline: !isExcluded,
                          sx: {
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            textDecoration: isExcluded ? 'line-through' : 'none',
                            '&:hover': !isExcluded ? {
                              backgroundColor: '#f3f4f6',
                              borderRadius: 1,
                            } : {},
                            px: 1,
                          },
                        }}
                      />

                      {/* Type badge */}
                      <Chip
                        label={typeUitvoerder}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.75rem',
                          backgroundColor: isExcluded ? '#d1d5db' : (typeColors[typeUitvoerder] || '#e5e7eb'),
                          color: '#fff',
                          fontWeight: 500,
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                  </Paper>
                );
              })}
            </Box>

            {!templateDetails && !error && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Template details laden...
                </Typography>
              </Box>
            )}
            {templateDetails && (!templateDetails.tasks || templateDetails.tasks.length === 0) && !error && (
              <Alert severity="warning">
                Deze template bevat geen taken. Kies een andere template.
              </Alert>
            )}
            {sortedTasks.length === 0 && allSortedTasks.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Je hebt alle taken verwijderd. Herstel minimaal één taak om door te gaan.
              </Alert>
            )}
          </Box>
        )}

        {/* Step 3: Planning - Date range & time distribution */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
              Kies de start- en einddatum voor de planning:
            </Typography>

            {/* Warning if deadline is in the past */}
            {deadlineInPast && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <strong>Let op:</strong> De deadline van deze deliverable ligt in het verleden. De einddatum is ingesteld op 2 weken vanaf vandaag. Pas dit aan indien nodig.
              </Alert>
            )}

            {/* Warning if no deadline was set on deliverable */}
            {deadlineMissing && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Let op:</strong> Deze deliverable heeft geen deadline. De einddatum is standaard ingesteld op 2 weken vanaf vandaag. Pas dit aan indien nodig.
              </Alert>
            )}

            <KlantplanningImport
              projectId={projectId}
              projectNummer={project?.fields?.Projectnummer || project?.projectNumber || ''}
              deliverableNaam={deliverableName}
              huidig={{ startDate, endDate, feedbackDays }}
              onToepassen={pasKlantplanningToe}
            />

            {/* Date range */}
            <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                Periode
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Startdatum"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={vroegsteStartDatum ? { min: vroegsteStartDatum } : undefined}
                    helperText={laatsteDraaidag ? `start ná draaidag ${korteDatum(laatsteDraaidag)}` : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Einddatum (deadline)"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Totaal: <strong>{workingDays}</strong> werkdagen (weekenden overgeslagen)
              </Typography>
              {startDate && endDate && startDate > endDate && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  De deadline ligt vóór de startdatum{laatsteDraaidag ? ' (de draaidag is pas daarna)' : ''}. Kies een latere deadline of overleg met de klant.
                </Alert>
              )}
              {laatsteDraaidag && startDate && startDate <= laatsteDraaidag && (
                <Alert
                  severity="warning"
                  sx={{ mt: 1.5 }}
                  action={vroegsteStartDatum ? (
                    <Button color="inherit" size="small" onClick={() => setStartDate(vroegsteStartDatum)}>
                      Start {korteDatum(vroegsteStartDatum)}
                    </Button>
                  ) : null}
                >
                  Postproductie start dan vóór of op de draaidag ({korteDatum(laatsteDraaidag)}). Er is nog niets om te monteren.
                </Alert>
              )}

              {andereDeliverables.length > 0 && (
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed #e5e7eb' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Deze planning ook gebruiken voor…
                    </Typography>
                    <Button size="small" variant="text" sx={{ fontSize: '0.7rem', color: '#6b7280' }}
                      onClick={() => setKopieerNaar(
                        gekozenDeliverables.length === andereDeliverables.length
                          ? {}
                          : Object.fromEntries(andereDeliverables.map((d) => [d.id, true])))}>
                      {gekozenDeliverables.length === andereDeliverables.length ? 'Geen' : 'Alles'}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Elke deliverable wordt gepland tot zijn eigen deadline; de uren en het sjabloon zijn hetzelfde.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, maxHeight: 190, overflowY: 'auto' }}>
                    {andereDeliverables.map((d) => {
                      const deadline = deadlineVoor(d);
                      const aan = !!kopieerNaar[d.id];
                      return (
                        <Box key={d.id} onClick={() => setKopieerNaar((v) => ({ ...v, [d.id]: !v[d.id] }))}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.25, borderRadius: 1,
                            cursor: 'pointer', border: '1px solid', borderColor: aan ? '#bfdbfe' : 'transparent',
                            backgroundColor: aan ? '#eff6ff' : 'transparent',
                          }}>
                          <Checkbox size="small" checked={aan} sx={{ p: 0.5 }}
                            onChange={() => setKopieerNaar((v) => ({ ...v, [d.id]: !v[d.id] }))}
                            onClick={(e) => e.stopPropagation()} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" noWrap>{deliverableNaam(d)}</Typography>
                          </Box>
                          <Typography variant="caption" sx={{ flexShrink: 0, color: deadline.eigen ? '#475569' : '#b45309' }}>
                            {deadline.eigen
                              ? `deadline ${deadline.datum}`
                              : `${deadline.verlopen ? 'deadline verlopen' : 'geen deadline'} → ${deadline.datum}`}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Vaste dagen van de klant: per mijlpaal de taak die dan klaar moet zijn */}
            {klantPins.length > 0 && (
              <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', mb: 3 }} data-testid="vaste-dagen">
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Vaste dagen van de klant
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  De gekozen taak moet op of vóór die dag klaar zijn; de werkdagen ertussen worden per stuk verdeeld.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {klantPins.map((pin) => {
                    const teLaat = pin.taakId ? planUitkomst.pinOverschrijdingen[pin.taakId] : 0;
                    return (
                      <Box key={pin.mijlpaalId}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ width: 190, flexShrink: 0 }}>
                            {pin.label || soortLabel(pin.soort)} · <strong>{korteDatum(pin.datum)}</strong>
                          </Typography>
                          <Select size="small" value={pin.taakId} displayEmpty
                            inputProps={{ 'aria-label': `Taak bij ${pin.label || soortLabel(pin.soort)}` }}
                            onChange={(e) => setPinKoppeling((k) => ({ ...k, [pin.mijlpaalId]: e.target.value }))}
                            sx={{ minWidth: 220, fontSize: '0.85rem' }}>
                            <MenuItem value=""><em>niet vastzetten</em></MenuItem>
                            {planTaken.map((t) => (
                              <MenuItem key={t.id} value={t.id} sx={{ fontSize: '0.85rem' }}>
                                {pin.soort === 'feedback' ? `${t.name} (klaar vóór feedback)` : t.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                        {teLaat > 0 && (
                          <Typography variant="caption" sx={{ color: '#b45309', display: 'block', mt: 0.25 }}>
                            Past niet: {teLaat} werkdag{teLaat === 1 ? '' : 'en'} te weinig vóór {korteDatum(pin.datum)}.
                            Verkort taken, minder feedbackdagen, of overleg met de klant.
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            )}

            {/* Feedback days between versions */}
            <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Feedback rondes
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tussen versies (v intern → v1 → v2 → DEF) wordt automatisch een feedback periode ingepland.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  label="Feedback dagen"
                  type="number"
                  value={feedbackDays === '' ? '' : feedbackDays}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setFeedbackDays('');
                    } else {
                      setFeedbackDays(Math.max(1, Math.min(10, parseInt(raw) || 1)));
                    }
                  }}
                  onBlur={() => {
                    if (feedbackDays === '' || feedbackDays < 1) setFeedbackDays(1);
                  }}
                  size="small"
                  inputProps={{ min: 1, max: 10 }}
                  sx={{ width: 130 }}
                />
                <Typography variant="caption" color="text.secondary">
                  werkdagen tussen elke versie (min 1, max 10)
                </Typography>
              </Box>

              {/* Wat de feedbackrondes kosten aan werkdagen */}
              {totalFeedbackDays > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {feedbackTransitions} feedbackronde{feedbackTransitions > 1 ? 's' : ''} × {parseInt(feedbackDays) || 0} dagen
                  = {totalFeedbackDays} dagen wachten. Over voor het werk: <strong>{Math.max(0, workingDays - totalFeedbackDays - puzzelWachtdagen)}</strong> van {workingDays} werkdagen
                  {workingDays - totalFeedbackDays - puzzelWachtdagen < sortedTasks.length && ' — te krap: elke taak krijgt dan één dag en de planning loopt over de deadline.'}
                </Typography>
              )}
            </Paper>

            {/* Automatic time distribution preview based on ZwaarteStap */}
            <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Tijdsverdeling (automatisch)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {totalFeedbackDays > 0 && (
                    <Chip
                      label={`${totalFeedbackDays}d feedback`}
                      size="small"
                      sx={{ backgroundColor: '#f3f4f6', color: '#6b7280', fontWeight: 500, fontSize: '0.7rem' }}
                    />
                  )}
                  <Chip
                    label={`${effectiveWorkingDays} werkdagen`}
                    size="small"
                    sx={{ backgroundColor: '#10b981', color: '#fff', fontWeight: 600 }}
                  />
                </Box>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                Dagen worden verdeeld op basis van ZwaarteStap per taak
                {(totalFeedbackDays + puzzelWachtdagen) > 0 && ` (${workingDays} totaal − ${totalFeedbackDays + puzzelWachtdagen} wachten = ${effectiveWorkingDays} voor taken)`}.
                Pas de waarden aan om de verdeling te wijzigen.
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 250, overflow: 'auto' }}>
                {sortedTasks.map(task => {
                  const taskName = task.fields?.Name || 'Taak';
                  const zwaarte = getZwaarteStap(task);
                  const typeUitvoerder = task.fields?.['Type Uitvoerder'] || 'Algemeen';
                  const estimatedDays = dagenPerTaak[task.id] || 1;

                  return (
                    <Box
                      key={task.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                        px: 1.5,
                        borderRadius: 1,
                        backgroundColor: '#f9fafb',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: typeColors[typeUitvoerder] || '#6b7280',
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" fontWeight={500} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {taskName}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                        <TextField
                          type="number"
                          value={customZwaarteStap[task.id] !== undefined ? customZwaarteStap[task.id] : zwaarte}
                          onChange={(e) => {
                            const val = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                            setCustomZwaarteStap(prev => ({ ...prev, [task.id]: val }));
                          }}
                          onBlur={(e) => {
                            // Ensure minimum of 1 when leaving the field
                            const val = Math.max(1, parseInt(e.target.value) || 1);
                            setCustomZwaarteStap(prev => ({ ...prev, [task.id]: val }));
                          }}
                          size="small"
                          inputProps={{ min: 1, max: 20, style: { textAlign: 'center', padding: '4px 8px' } }}
                          sx={{ width: 60, '& .MuiOutlinedInput-root': { height: 28 } }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 55, textAlign: 'right' }}>
                          ~{estimatedDays} {estimatedDays === 1 ? 'dag' : 'dagen'}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Paper>

            {/* Extra taken toevoegen */}
            <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', mt: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                Extra taken toevoegen
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Selecteer welke extra taken automatisch toegevoegd moeten worden:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ondertitelNodig}
                      onChange={(e) => setOndertitelNodig(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">🔤 Ondertiteling</Typography>
                      {deliverable?.fields?.LengteVideo && (
                        <Chip
                          label={`~${Math.max(Math.ceil((deliverable.fields.LengteVideo || 0) / 60), 1)} uur`}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={thumbnailNodig}
                      onChange={(e) => setThumbnailNodig(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">🖼️ Thumbnail</Typography>
                      <Chip label="~2 uur" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={proxiesNodig}
                      onChange={(e) => setProxiesNodig(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">📁 Proxies maken</Typography>
                      <Chip label="~2 uur" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                    </Box>
                  }
                />
              </Box>
            </Paper>

            {/* Creative Lead Review */}
            <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', mt: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                Creative Lead Review
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Optioneel: Laat een Creative Lead meekijken bij regisseur-review stappen.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={creativeleadEnabled}
                    onChange={(e) => setCreativeleadEnabled(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">🎬 Creative Lead meekijken</Typography>
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              {creativeleadEnabled && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <Autocomplete
                      value={creativeleadAssignee}
                      onChange={(_, newValue) => setCreativeleadAssignee(newValue)}
                      options={personen.filter(p => {
                        // Filter for Creative leads (Creatives / Regisseurs)
                        const functies = p.fields?.Functie || [];
                        const functieArray = Array.isArray(functies) ? functies : [functies];
                        return functieArray.some(f =>
                          f && (f.toLowerCase().includes('creative') ||
                                f.toLowerCase().includes('regisseur') ||
                                f.toLowerCase().includes('director'))
                        );
                      }).length > 0
                        ? personen.filter(p => {
                            const functies = p.fields?.Functie || [];
                            const functieArray = Array.isArray(functies) ? functies : [functies];
                            return functieArray.some(f =>
                              f && (f.toLowerCase().includes('creative') ||
                                    f.toLowerCase().includes('regisseur') ||
                                    f.toLowerCase().includes('director'))
                            );
                          })
                        : personen
                      }
                      getOptionLabel={(option) => option?.fields?.['Volledige naam'] || option?.fields?.Naam || ''}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Creative Lead"
                          size="small"
                          placeholder="Selecteer persoon..."
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: typeColors['Creative'] || '#a855f7',
                                    mr: 1,
                                  }}
                                />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: typeColors['Creative'] || '#a855f7' }}>
                              {getInitials(option.fields?.['Volledige naam'] || option.fields?.Naam)}
                            </Avatar>
                            <Typography variant="body2">
                              {option.fields?.['Volledige naam'] || option.fields?.Naam}
                            </Typography>
                          </Box>
                        </li>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Uren"
                      type="number"
                      value={creativeleadHours}
                      onChange={(e) => setCreativeleadHours(Math.max(0.5, parseFloat(e.target.value) || 0.5))}
                      fullWidth
                      size="small"
                      inputProps={{ min: 0.5, step: 0.5 }}
                      helperText="Per review taak"
                    />
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Box>
        )}

        {/* Step 4: Uren - Hours input */}
        {activeStep === 4 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
              Vul de geoffreerde uren in voor automatische verdeling over taken:
            </Typography>

            <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Uren uit offerte
                </Typography>
                {project?.fields?.OfferteMontageAnimatieRegelsMetEnters && (
                  <Tooltip title="Bekijk offerteregels">
                    <IconButton
                      size="small"
                      onClick={(e) => setOfferteInfoAnchor(e.currentTarget)}
                      sx={{ color: '#3b82f6' }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Offerteregels Popover */}
              <Popover
                open={Boolean(offerteInfoAnchor)}
                anchorEl={offerteInfoAnchor}
                onClose={() => setOfferteInfoAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 2, maxWidth: 400, maxHeight: 300, overflow: 'auto' }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Offerteregels (Montage & Animatie)
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#374151' }}>
                    {project?.fields?.OfferteMontageAnimatieRegelsMetEnters || 'Geen offerteregels gevonden'}
                  </Typography>
                </Box>
              </Popover>

              {offerteGroepen === null && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>Offerteregels ophalen…</Typography>
              )}
              {offerteGroepen && offerteGroepen.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Geen offerteregels met montage- of animatie-uren gevonden voor dit project — vul de uren zelf in.
                </Typography>
              )}
              {offerteGroepen && offerteGroepen.length > 0 && (() => {
                const groep = offerteGroepen.find((g) => g.naam === offerteGroepNaam) || null;
                const n = Math.max(1, parseInt(offerteVerdeelOver, 10) || 1);
                const perMontage = groep ? Math.round((groep.montage / n) * 2) / 2 : 0;
                const perAnimatie = groep ? Math.round((groep.animatie / n) * 2) / 2 : 0;
                return (
                  <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#475569', mb: 1 }}>
                      Uit de offerte (Gripp) — kies de sectie waar deze deliverable onder valt
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TextField select size="small" value={offerteGroepNaam} sx={{ minWidth: 260 }}
                        onChange={(e) => { const g = offerteGroepen.find((x) => x.naam === e.target.value); setOfferteGroepNaam(e.target.value); if (g) setOfferteVerdeelOver(g.aantal || 1); }}>
                        {offerteGroepen.map((g) => (
                          <MenuItem key={g.naam} value={g.naam}>
                            {g.naam} — {g.montage ? `${g.montage}u montage` : ''}{g.montage && g.animatie ? ', ' : ''}{g.animatie ? `${g.animatie}u animatie` : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Typography variant="body2" color="text.secondary">verdeeld over</Typography>
                      <TextField size="small" type="number" value={offerteVerdeelOver} onChange={(e) => setOfferteVerdeelOver(e.target.value)}
                        inputProps={{ min: 1, step: 1, style: { width: 48, textAlign: 'center' } }} />
                      <Typography variant="body2" color="text.secondary">
                        deliverable{n === 1 ? '' : 's'}{groep?.telEenheid ? ` (offerte: ${groep.aantal} ${groep.telEenheid})` : ''}
                      </Typography>
                    </Box>
                    {groep && (
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 1.25, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Per deliverable: {perMontage}u montage{perAnimatie ? ` · ${perAnimatie}u animatie` : ''}
                        </Typography>
                        <Button size="small" variant="contained" sx={{ textTransform: 'none', fontWeight: 700 }}
                          onClick={() => { setUrenMontage(perMontage ? String(perMontage) : ''); setUrenAnimatie(perAnimatie ? String(perAnimatie) : ''); setCustomTaskHours({}); }}>
                          Gebruik deze uren
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          Kopieer je deze planning naar meer deliverables uit dezelfde sectie, dan krijgt elk dit aantal.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })()}

              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Uren Montage"
                    type="number"
                    value={urenMontage}
                    onChange={(e) => setUrenMontage(e.target.value)}
                    fullWidth
                    inputProps={{ min: 0, step: 0.5 }}
                    InputProps={{
                      startAdornment: (
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: typeColors['Editor'] || '#3b82f6',
                            mr: 1,
                          }}
                        />
                      ),
                    }}
                    helperText="Voor Editor taken"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Uren Animatie"
                    type="number"
                    value={urenAnimatie}
                    onChange={(e) => setUrenAnimatie(e.target.value)}
                    fullWidth
                    inputProps={{ min: 0, step: 0.5 }}
                    InputProps={{
                      startAdornment: (
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: typeColors['Animator'] || '#ec4899',
                            mr: 1,
                          }}
                        />
                      ),
                    }}
                    helperText="Voor Animator taken"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Uren Preproductie"
                    type="number"
                    value={urenPreproductie}
                    onChange={(e) => setUrenPreproductie(e.target.value)}
                    fullWidth
                    inputProps={{ min: 0, step: 0.5 }}
                    InputProps={{
                      startAdornment: (
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: typeColors['Projectleider'] || '#10b981',
                            mr: 1,
                          }}
                        />
                      ),
                    }}
                    helperText="Voor Projectleider taken"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Hour distribution preview per task - EDITABLE */}
            {(parseFloat(urenMontage) > 0 || parseFloat(urenAnimatie) > 0 || parseFloat(urenPreproductie) > 0) && (
              <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Urenverdeling per taak
                  </Typography>
                  {Object.keys(customTaskHours).length > 0 && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setCustomTaskHours({})}
                      sx={{ fontSize: '0.7rem', color: '#6b7280' }}
                    >
                      Reset naar automatisch
                    </Button>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Uren worden verdeeld naar zwaarte (in halve uren) en tellen samen exact op tot de geoffreerde uren. Klik op de uren om aan te passen:
                </Typography>
                {plannedTasks.some((t) => !t.hours && URENPOT_PER_TYPE[t.type] && potUren[URENPOT_PER_TYPE[t.type]] > 0) && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Te weinig uren voor het aantal taken: sommige taken krijgen 0 uur. Verhoog de uren of vul ze per taak in.
                  </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 250, overflow: 'auto' }}>
                  {plannedTasks.map(task => {
                    const typeColor = typeColors[task.type] || '#6b7280';
                    const isCustom = customTaskHours[task.id] !== undefined;
                    return (
                      <Box
                        key={task.id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.75,
                          px: 1.5,
                          borderRadius: 1,
                          backgroundColor: isCustom ? '#fefce8' : '#f9fafb',
                          border: isCustom ? '1px solid #fde68a' : '1px solid transparent',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: typeColor,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1 }}>
                            {task.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                          <Chip
                            label={task.type}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              backgroundColor: typeColor,
                              color: '#fff',
                              fontWeight: 500,
                            }}
                          />
                          <TextField
                            type="number"
                            value={customTaskHours[task.id] !== undefined ? customTaskHours[task.id] : task.hours}
                            onChange={(e) => {
                              // Allow empty string while typing
                              const val = e.target.value === '' ? '' : parseFloat(e.target.value);
                              if (val === '' || (!isNaN(val) && val >= 0)) {
                                setCustomTaskHours(prev => ({ ...prev, [task.id]: val }));
                              }
                            }}
                            onBlur={(e) => {
                              // On blur, ensure minimum of 1
                              const val = Math.max(1, parseFloat(e.target.value) || 1);
                              setCustomTaskHours(prev => ({ ...prev, [task.id]: val }));
                            }}
                            size="small"
                            inputProps={{
                              min: 0,
                              step: 1,
                              style: {
                                textAlign: 'center',
                                padding: '3px 4px',
                                fontWeight: 700,
                                fontSize: '0.8rem',
                                color: isCustom ? '#b45309' : '#1d4ed8',
                              },
                            }}
                            sx={{
                              width: 56,
                              '& .MuiOutlinedInput-root': {
                                height: 26,
                                backgroundColor: isCustom ? '#fef3c7' : '#dbeafe',
                                '& fieldset': { borderColor: isCustom ? '#f59e0b' : '#93c5fd' },
                              },
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 10 }}>u</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 35, textAlign: 'right' }}>
                            {task.days}d
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {/* Totals per type */}
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {/* Per urenpot, niet per rol: twee rollen op dezelfde pot
                      (Editor + Algemeen) telden de pot anders dubbel. */}
                  {Object.entries(potUren).map(([pot, potTotaal]) => {
                    if (!(potTotaal > 0)) return null;
                    const takenInPot = plannedTasks.filter(t => URENPOT_PER_TYPE[t.type] === pot);
                    if (!takenInPot.length) return null;
                    const verdeeld = takenInPot.reduce((sum, t) => sum + (parseFloat(t.hours) || 0), 0);
                    const potColor = typeColors[takenInPot[0].type] || '#6b7280';
                    const verschil = Math.round((verdeeld - potTotaal) * 10) / 10;

                    return (
                      <Box key={pot} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: potColor }} />
                        <Typography variant="caption" fontWeight={600}>
                          {pot.charAt(0).toUpperCase() + pot.slice(1)}: {Math.round(verdeeld * 10) / 10}u / {potTotaal}u
                        </Typography>
                        {verschil !== 0 && (
                          <Typography variant="caption" sx={{ color: '#f59e0b' }}>
                            (Δ{verschil > 0 ? '+' : ''}{verschil}u)
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              Deze uren worden verdeeld over de taken op basis van hun ZwaarteStap. Laat leeg om geen UrenWerk in te vullen.
            </Alert>
          </Box>
        )}

        {/* Step 5: Uitvoerders - Assignees with Capacity */}
        {activeStep === 5 && (
          <Box>
            {/* Planning mode keuze */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #e5e7eb', bgcolor: '#f8fafc' }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Hoe wil je de taken toewijzen?
              </Typography>
              <RadioGroup
                value={planningMode}
                onChange={(e) => setPlanningMode(e.target.value)}
              >
                <FormControlLabel
                  value="zelf"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Zelf inplannen
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Je selecteert uitvoerders per taaktype. Gebruik de Planning Puzzel om beschikbaarheid te bekijken.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                />
                <FormControlLabel
                  value="planner"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Planner laat inplannen
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Taken verschijnen in Planner To-Do. Geef suggesties mee voor de planner.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </RadioGroup>
            </Paper>

            {/* Planning Puzzel button - altijd zichtbaar */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CalendarViewWeekIcon />}
                onClick={() => setPuzzleDialogOpen(true)}
                disabled={loadingCapacity || Object.keys(capacityData).length === 0}
                sx={{ borderRadius: 2 }}
              >
                Planning Puzzel
              </Button>
            </Box>

            {loadingCapacity && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Beschikbaarheid wordt geladen...
              </Alert>
            )}

            {!loadingCapacity && Object.keys(capacityData).length > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Beschikbaarheid geladen voor {startDate} t/m {endDate} ({workingDays} werkdagen). Personen gesorteerd op beschikbaarheid.
              </Alert>
            )}

            {/* Uitvoerders per type - alleen bij 'zelf inplannen' */}
            {planningMode === 'zelf' && (uniqueTaskTypes.length > 0 ? (
              <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                  Uitvoerders per type
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                  Selecteer per type wie de taken uitvoert. Bezetting toont % gepland in deze periode.
                </Typography>

                <Grid container spacing={2}>
                  {uniqueTaskTypes.map(type => {
                    // Get capacity data for this role
                    const roleCapacity = capacityData[type] || [];

                    // Build options: matched persons first (by capacity or functie), then remaining personen
                    let options;
                    const matchedIds = new Set();
                    if (roleCapacity.length > 0) {
                      // Map capacity data back to personen objects (already sorted by capacity from backend)
                      options = roleCapacity.map(cap => {
                        const person = personen.find(p => p.id === cap.id);
                        if (cap._matched) matchedIds.add(cap.id);
                        return person ? { ...person, _capacity: cap } : null;
                      }).filter(Boolean);
                    } else {
                      // Fallback: filter personen by functie, then add the rest
                      const matched = personen.filter(p => {
                        const functies = p.fields?.Functie || [];
                        const functieArray = Array.isArray(functies) ? functies : [functies];
                        const isMatch = functieArray.some(f => f && f.toLowerCase().includes(type.toLowerCase()));
                        if (isMatch) matchedIds.add(p.id);
                        return isMatch;
                      });
                      const rest = personen.filter(p => !matchedIds.has(p.id));
                      options = matched.length > 0 ? [...matched, ...rest] : personen;
                    }

                    return (
                      <Grid item xs={12} sm={6} key={type}>
                        <Autocomplete
                          value={typeAssignees[type] || null}
                          onChange={(_, newValue) => {
                            setTypeAssignees(prev => ({ ...prev, [type]: newValue }));
                          }}
                          options={options}
                          getOptionLabel={(option) => option.fields?.['Volledige naam'] || option.fields?.Naam || ''}
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={type}
                              size="small"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: typeColors[type] || '#6b7280',
                                        mr: 1,
                                      }}
                                    />
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          groupBy={(option) => {
                            if (matchedIds.size > 0 && matchedIds.size < personen.length) {
                              return matchedIds.has(option.id) ? 'Aanbevolen' : 'Overige medewerkers';
                            }
                            return '';
                          }}
                          renderOption={(props, option) => {
                            const capacity = option._capacity || capacityData[type]?.find(c => c.id === option.id);
                            const percentage = capacity?.capacityPercentage ?? null;
                            const availableHours = capacity?.availableHours ?? null;

                            return (
                              <li {...props}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
                                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: typeColors[type] || '#6b7280' }}>
                                    {getInitials(option.fields?.['Volledige naam'] || option.fields?.Naam)}
                                  </Avatar>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={500} noWrap>
                                      {option.fields?.['Volledige naam'] || option.fields?.Naam}
                                    </Typography>
                                    {availableHours !== null && (
                                      <Typography variant="caption" color="text.secondary">
                                        {availableHours > 0 ? `${availableHours}u beschikbaar` : 'Geen beschikbaarheid'}
                                      </Typography>
                                    )}
                                  </Box>
                                  {percentage !== null && (
                                    <Chip
                                      label={`${percentage}%`}
                                      size="small"
                                      sx={{
                                        height: 22,
                                        minWidth: 50,
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        backgroundColor: getCapacityBgColor(percentage),
                                        color: getCapacityColor(percentage),
                                      }}
                                    />
                                  )}
                                </Box>
                              </li>
                            );
                          }}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            ) : (
              <Alert severity="info">
                Geen specifieke uitvoerder-types gevonden in de gekozen template.
              </Alert>
            ))}

            {/* Per-task assignee overrides - alleen bij zelf inplannen */}
            {planningMode === 'zelf' && plannedTasks.length > 0 && (
              <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', mt: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Uitvoerder per taak (optioneel)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Standaard krijgt elke taak de uitvoerder van het type hierboven.
                  Wil je per versie iemand anders? Kies hier per taak:
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {plannedTasks.map(task => {
                    const typeColor = typeColors[task.type] || '#6b7280';
                    const hasOverride = !!taskAssigneeOverrides[task.id];
                    const roleDefault = typeAssignees[task.type] || null;
                    const effectiveAssignee = taskAssigneeOverrides[task.id] || roleDefault;

                    // Build options list same as role-level: matched first, then rest
                    const roleCapacity = capacityData[task.type] || [];
                    let options;
                    if (roleCapacity.length > 0) {
                      const capIds = new Set();
                      const matched = roleCapacity.map(cap => {
                        const person = personen.find(p => p.id === cap.id);
                        if (person) capIds.add(person.id);
                        return person ? { ...person, _capacity: cap } : null;
                      }).filter(Boolean);
                      const rest = personen.filter(p => !capIds.has(p.id));
                      options = [...matched, ...rest];
                    } else {
                      options = personen;
                    }

                    return (
                      <Box
                        key={task.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          py: 0.5,
                          px: 1,
                          borderRadius: 1,
                          backgroundColor: hasOverride ? '#fefce8' : '#f9fafb',
                          border: hasOverride ? '1px solid #fde68a' : '1px solid transparent',
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: typeColor, flexShrink: 0 }} />
                        <Typography variant="body2" fontWeight={500} noWrap sx={{ minWidth: 140, flex: '0 0 140px' }}>
                          {task.name}
                        </Typography>
                        <Chip label={task.type} size="small" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: typeColor, color: '#fff' }} />
                        <Autocomplete
                          value={effectiveAssignee}
                          onChange={(_, newValue) => {
                            if (!newValue || (roleDefault && newValue.id === roleDefault.id)) {
                              // Remove override if same as role default
                              setTaskAssigneeOverrides(prev => {
                                const next = { ...prev };
                                delete next[task.id];
                                return next;
                              });
                            } else {
                              setTaskAssigneeOverrides(prev => ({ ...prev, [task.id]: newValue }));
                            }
                          }}
                          options={options}
                          getOptionLabel={(option) => option?.fields?.['Volledige naam'] || option?.fields?.Naam || ''}
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                          size="small"
                          sx={{ flex: 1, minWidth: 180 }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={roleDefault ? `Standaard: ${roleDefault.fields?.['Volledige naam'] || ''}` : 'Kies uitvoerder'}
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  height: 32,
                                  fontSize: '0.8rem',
                                },
                              }}
                            />
                          )}
                          renderOption={(props, option) => {
                            const capacity = option._capacity || capacityData[task.type]?.find(c => c.id === option.id);
                            const percentage = capacity?.capacityPercentage ?? null;
                            return (
                              <li {...props}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                                  <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                    {option.fields?.['Volledige naam'] || option.fields?.Naam}
                                  </Typography>
                                  {percentage !== null && (
                                    <Chip
                                      label={`${percentage}%`}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        backgroundColor: getCapacityBgColor(percentage),
                                        color: getCapacityColor(percentage),
                                      }}
                                    />
                                  )}
                                </Box>
                              </li>
                            );
                          }}
                        />
                        {hasOverride && (
                          <Chip
                            label="Aangepast"
                            size="small"
                            onDelete={() => {
                              setTaskAssigneeOverrides(prev => {
                                const next = { ...prev };
                                delete next[task.id];
                                return next;
                              });
                            }}
                            sx={{ height: 20, fontSize: '0.6rem', backgroundColor: '#fef3c7', color: '#b45309' }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            )}

            {/* Suggesties voor planner en opmerkingen */}
            <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                Voor de planner (optioneel)
              </Typography>

              {/* Ideale personen per rol - suggesties */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Geef aan welke personen je als projectleider ideaal zou vinden. De planner kan hier rekening mee houden.
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {uniqueTaskTypes.map(type => {
                  const roleCapacity = capacityData[type] || [];
                  let options;
                  if (roleCapacity.length > 0) {
                    options = roleCapacity.map(cap => {
                      const person = personen.find(p => p.id === cap.id);
                      return person ? { ...person, _capacity: cap } : null;
                    }).filter(Boolean);
                  } else {
                    options = personen;
                  }

                  return (
                    <Grid item xs={12} sm={6} key={`ideal-${type}`}>
                      <Autocomplete
                        multiple
                        value={(idealAssignees[type] || []).map(id => options.find(o => o.id === id)).filter(Boolean)}
                        onChange={(_, newValue) => {
                          setIdealAssignees(prev => ({
                            ...prev,
                            [type]: newValue.map(p => p.id),
                          }));
                        }}
                        options={options}
                        getOptionLabel={(option) => option?.fields?.['Volledige naam'] || option?.fields?.Naam || ''}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        size="small"
                        limitTags={2}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`Ideaal voor ${type}`}
                            placeholder="Suggesties..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      backgroundColor: typeColors[type] || '#6b7280',
                                      mr: 0.5,
                                      flexShrink: 0,
                                    }}
                                  />
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => {
                          const capacity = option._capacity;
                          return (
                            <li {...props}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {option?.fields?.['Volledige naam'] || option?.fields?.Naam}
                                </Typography>
                                {capacity?.capacityPercentage != null && (
                                  <Chip
                                    label={`${capacity.capacityPercentage}%`}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: '0.65rem',
                                      backgroundColor: capacity.capacityPercentage > 100 ? '#fee2e2' : capacity.capacityPercentage > 80 ? '#fef3c7' : '#dcfce7',
                                      color: capacity.capacityPercentage > 100 ? '#991b1b' : capacity.capacityPercentage > 80 ? '#92400e' : '#166534',
                                    }}
                                  />
                                )}
                              </Box>
                            </li>
                          );
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>

              {/* Opmerkingen voor planner */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Opmerkingen voor de planner (bijv. deadlines, voorkeuren, bijzonderheden):
              </Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                value={planningNotes}
                onChange={(e) => setPlanningNotes(e.target.value)}
                placeholder="Bijv: Deadline is flexibel, Johan heeft voorkeur omdat hij eerder aan dit project werkte, etc."
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.85rem',
                  },
                }}
              />
            </Paper>

            {/* Planning Puzzle Dialog */}
            <PlanningPuzzleDialog
              open={puzzleDialogOpen}
              onClose={() => setPuzzleDialogOpen(false)}
              onApply={(newAssignments) => {
                // Extract all puzzle data from result
                const {
                  _taskAssignments,
                  _taskDurationOverrides,
                  _taskOffsets,
                  _feedbackDays,
                  _planningOffset,
                  _taskHoursOverrides,
                  ...roleAssignments
                } = newAssignments;

                // Role-level assignments
                setTypeAssignees(prev => ({ ...prev, ...roleAssignments }));

                // Per-task assignee overrides
                if (_taskAssignments) {
                  setTaskAssigneeOverrides(prev => ({ ...prev, ..._taskAssignments }));
                }

                // Puzzle adjustments
                if (_taskDurationOverrides) {
                  setPuzzleDurationOverrides(_taskDurationOverrides);
                }
                if (_taskOffsets) {
                  setPuzzleTaskOffsets(_taskOffsets);
                }
                if (_feedbackDays !== undefined) {
                  setFeedbackDays(_feedbackDays);
                }
                if (_planningOffset !== undefined) {
                  setPuzzlePlanningOffset(_planningOffset);
                }
                if (_taskHoursOverrides) {
                  setPuzzleHoursOverrides(_taskHoursOverrides);
                }

                setPuzzleDialogOpen(false);
              }}
              startDate={startDate}
              endDate={endDate}
              tasks={plannedTasks}
              capacityData={capacityData}
              personen={personen}
              deliverableName={deliverable?.fields?.['Naam van Deliverable'] || 'Deliverable'}
              projectName={project?.fields?.Projectnr_en_naam || project?.fields?.Projectnaam || ''}
              defaultFeedbackDays={feedbackDays}
              reviewDag={creativeleadEnabled}
              klantPins={klantPins.filter((p) => p.taakId)}
              beginDuren={puzzleDurationOverrides}
              beginOffsets={puzzleTaskOffsets}
              beginPlanningOffset={puzzlePlanningOffset}
              beginToewijzingen={Object.fromEntries(Object.entries(taskAssigneeOverrides)
                .map(([taakId, persoon]) => [taakId, persoon ? persoon.id : '__UNASSIGNED__']))}
              beginRollen={Object.fromEntries(Object.entries(typeAssignees)
                .filter(([, persoon]) => persoon?.id).map(([rol, persoon]) => [rol, persoon.id]))}
            />
          </Box>
        )}

        {/* Step 6: Result */}
        {activeStep === 6 && result && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#dcfce7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48, color: '#10b981' }} />
            </Box>

            <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
              Planning Gegenereerd!
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              De postproductie taken zijn succesvol aangemaakt
            </Typography>

            <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: '#f9fafb', maxWidth: 400, margin: '0 auto' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#3b82f6' }}>
                    {result.created}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    taken
                  </Typography>
                </Box>
                {result.subtasksCreated > 0 && (
                  <Box>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#10b981' }}>
                      {result.subtasksCreated}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      subtaken
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Eerlijk melden als het werk niet vóór de deadline past — het werd
                hiervoor stilzwijgend over de deadline heen gepland. */}
            {result.deadlineOverschrijding > 0 && (
              <Alert severity="warning" sx={{ mt: 2, textAlign: 'left', maxWidth: 520, mx: 'auto' }}>
                De planning past niet vóór de deadline van {result.deadline}: de laatste taak eindigt{' '}
                <strong>{result.deadlineOverschrijding} werkdag{result.deadlineOverschrijding === 1 ? '' : 'en'}</strong> later.
                De taken zijn al zo kort mogelijk gemaakt — er is meer tijd nodig, of er moet werk uit.
              </Alert>
            )}

            {result.klantpinsTeLaat?.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2, textAlign: 'left', maxWidth: 520, mx: 'auto' }}>
                Niet elke datum van de klant is te halen:
                {result.klantpinsTeLaat.map((p) => (
                  <Box key={p.taakId}>{p.taak}: klaar {korteDatum(p.eind)}, {p.werkdagen} werkdag{p.werkdagen === 1 ? '' : 'en'} te laat.</Box>
                ))}
              </Alert>
            )}

            {result.extraDeliverables?.length > 0 && (
              <Paper variant="outlined" sx={{ mt: 2, p: 2, textAlign: 'left', maxWidth: 520, mx: 'auto' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Dezelfde planning op de gekozen deliverables
                </Typography>
                {result.extraDeliverables.map((d, i) => (
                  <Typography key={i} variant="body2" sx={{ color: d.fout ? '#b91c1c' : 'text.secondary' }}>
                    {d.fout ? '✕' : '✓'} {d.naam}
                    {d.fout
                      ? ` — ${d.fout}`
                      : ` — ${d.created} taken tot ${d.deadline}${d.eigenDeadline ? '' : ' (eigen deadline ontbrak of was verlopen)'}${d.sectie ? ` · ${d.uren}u uit "${d.sectie}"` : ''}${d.overschrijding ? ` — ${d.overschrijding} dag(en) over de deadline` : ''}`}
                  </Typography>
                ))}
              </Paper>
            )}

            {!(result.deadlineOverschrijding > 0 || result.extraDeliverables?.length > 0 || result.klantpinsTeLaat?.length > 0) && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Dit venster sluit automatisch...
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb' }}>
        {activeStep > 0 && activeStep < 6 && (
          <Button onClick={() => setActiveStep(prev => prev - 1)}>
            Vorige
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {activeStep < 6 && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceed() || generating}
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> :
                      activeStep === 5 ? <PlaylistAddIcon /> : null}
            sx={{ borderRadius: 2 }}
          >
            {generating ? 'Genereren...' : activeStep === 5 ? 'Taken Genereren' : 'Volgende'}
          </Button>
        )}
        {activeStep === 6 && (
          <Button variant="contained" onClick={onClose} sx={{ borderRadius: 2 }}>
            Sluiten
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default TaskTemplateGenerator;
